import React from "react";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import Paper from "@material-ui/core/Paper";
import Draggable from "react-draggable";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import templateService from "../services/TemplateService";
import notify from "../utils/notifier";
import IconButton from "@material-ui/core/IconButton";
import Util from "../utils/Util";
import { useTranslation } from "react-i18next";
import SettingsIcon from "@material-ui/icons/Settings";
import Tooltip from "@material-ui/core/Tooltip";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import InputLabel from "@material-ui/core/InputLabel";
import  { useState } from 'react';
// Import privileges utility
import { hasPrivilege } from "../utils/privileges_utility";

const useStyles = makeStyles(theme => ({
  button: {
    margin: theme.spacing(1)
  },
  flexItem: {
    flex: 1
  },
  textField: {
    // paddingRight: theme.spacing(4)
    marginTop: 0,
    paddingBottom: 15
  },
  disabledIcon: {
    opacity: 0.5,
    cursor: 'not-allowed'
  }
}));

function PaperComponent(props) {
  return (
    <Draggable cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} />
    </Draggable>
  );
}

export default function GenerateSampleModal(props) {
  const [t, i18n] = useTranslation();
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);

  const [row, setRow] = React.useState('');
  const [rowId, setRowId] = React.useState('');
  const [outputType, setOutputType] = React.useState({ value: "pdf" });
  
  // Check if this is for HTML templates
  const isHtmlTemplate = props.isHtmlTemplate || false;

  const [metadata, setMetadata] = React.useState({
    value: ''
  });

  // Check if user has generate privilege
  const canGenerate = hasPrivilege("DOC_TEMP_GENERATE");

  const handleClickOpen = () => {
    // If user doesn't have permission, show notification instead of opening dialog
    if (!canGenerate) {
      notify({
        type: "error",
        message: "You don't have permission to generate sample documents"
      });
      return;
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  React.useEffect(() => {
    setRow(props.row);
    setMetadata({
      value: Util.toString(props.row["metadata"])
    });
    setRowId(props.rowId);
  }, [props.rowId]);

  // Add state for vendor selection in your component
  const [vendorName, setVendorName] = useState({ value: "" });
  
  const generateSample = (templateId, row) => {
    // Double-check permission before generating
    if (!canGenerate) {
      notify({
        type: "error",
        message: "You don't have permission to generate sample documents"
      });
      handleClose();
      return;
    }
    
    let reqObj = {
      data: JSON.parse(metadata.value),
      options: {
        type: outputType.value
      }
    };
    
    // Pass the vendor name as an option to the service
    templateService.generateTemplate(templateId, reqObj, vendorName.value).then(
      response => {
        var disposition = response.headers["content-disposition"];
        let filename = Util.extractFileNameFromDisposition(disposition);
        const type = response.headers["content-type"];
        const blob = new Blob([response.data]);
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        handleClose();
      },
      error => {
        notify({
          type: "error",
          message: "Error: " + error.response.data.errorCode
        });
      }
    );
  };

  return (
    <div>
      <Tooltip title={canGenerate ? "Generate Sample" : "Generate Sample (Requires permission)"} placement="top">
        <span className={!canGenerate ? classes.disabledIcon : ''}>
          <IconButton
            aria-label="generate"
            color="primary"
            size="small"
            onClick={handleClickOpen}
            disabled={!canGenerate}
          >
            <SettingsIcon fontSize="inherit" />
          </IconButton>
        </span>
      </Tooltip>
      <Dialog
        open={open}
        PaperComponent={PaperComponent}
        fullWidth={true}
        maxWidth="sm"
      >
        <DialogTitle style={{ cursor: "move" }} id="draggable-dialog-title">
          {t("Generate sample document")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("Please provide the metada to generate sample document")}
          </DialogContentText>
          <div>
            <InputLabel id="sample-metadata-label">{t("Metadata")}*</InputLabel>
            <TextField
              id="sample-metadata"
              // labelId="sample-metadata-label"
              multiline
              fullWidth
              required
              rows="4"
              className={classes.textField}
              margin="normal"
              value={metadata.value}
              onChange={e => {
                const val = e.target.value;
                setMetadata(prevState => {
                  return { ...prevState, value: val };
                });
              }}
            />
          </div>
          <div>
            <InputLabel id="output-filetype-label">
              {t("Output FileType")}*
            </InputLabel>
            <Select
              labelId="output-filetype-label"
              id="output-filetype"
              value={outputType.value}
              fullWidth
              disabled={isHtmlTemplate}
              onChange={e => {
                const val = e.target.value;
                setOutputType(prevState => {
                  return { ...prevState, value: val };
                });
              }}
            >
              <MenuItem value="pdf">PDF</MenuItem>
              {!isHtmlTemplate && <MenuItem value="docx">DOCX</MenuItem>}
            </Select>
          </div>
          {!isHtmlTemplate && (
            <div style={{    paddingTop: '20px'}}>
              <InputLabel id="vendor-selection-label">
                {t("Vendor (Optional)")}
              </InputLabel>
              <Select
                labelId="vendor-selection-label"
                id="vendor-selection"
                value={vendorName.value}
                fullWidth
                onChange={e => {
                  const val = e.target.value;
                  setVendorName(prevState => {
                    return { ...prevState, value: val };
                  });
                }}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="aspose">Aspose</MenuItem>
                <MenuItem value="apryse">Apryse</MenuItem>
              </Select>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleClose} color="secondary">
            {t("CANCEL")}
          </Button>
          <Button
            onClick={event => {
              generateSample(rowId, row);
            }}
            color="primary"
          >
            {t("SUBMIT")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}